import {
    faCalendarAlt,
    faMemoCircleCheck,
    faBellConcierge,
    faSyringe,
    faStar,
    faClockRotateLeft
} from '@fortawesome/pro-light-svg-icons';
import { faCircleXmark, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { makeStyles, Theme, createStyles, Box, Grid, Typography, CircularProgress } from '@material-ui/core';
import {
    AppointmentsStatus,
    BookingsState,
    BookingsStatus,
    getAppointmentThunk,
    getBookingThunk
} from '@spike/bookings-action';
import useNonInitialEffect from '@versiondos/hooks';
import { setNotificationBellAmount } from 'actions/notificationBell/NotificationBellActions';
import { updateThunk } from 'actions/userNotifications/userNotificationActions';
import { openWaitlistPanel } from 'actions/waitlists/WaitlistsActions';
import { UserNotificationSubtype, UserNotificationType } from 'model/UserNotifications';
import IconAvatar from 'components/UI/IconAvatar';
import QuillContent from 'components/UI/QuillContent';
import { AES } from 'crypto-js';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import useServiceCategories from 'hooks/useServiceCategories';
import { BookingDestination, getBookingDestination } from 'components/Bookings2/List/tabs';
import { UserNotification } from 'model/UserNotifications';
import moment from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { MarketingReviewsPath, VaccineApprovalsPath } from 'routes/Paths';
import { RootState } from 'store';
import Booking from '@spike/booking-model';
import NotificationRowActions from 'components/Notifications/UI/NotificationRowActions';
import Appointment, { status } from '@spike/appointment-model';
import NotificationRowRequestStatus from 'components/Notifications/UI/NotificationRowRequestStatus';
import NotificationRowVaccineApprovalStatus from 'components/Notifications/UI/NotificationRowVaccineApprovalStatus';
import { boldVaccineApprovalNames } from './boldVaccineApprovalNames';

const VACCINE_APPROVAL_SOURCE_LABELS: Record<string, string> = {
    pet_parent_portal: 'Pet Parent Portal',
    online_booking: 'Online Booking'
};

const getNotificationCategoryLabel = (notification: UserNotification): string => {
    if (notification.type === UserNotificationType.VACCINE_APPROVAL) {
        const sourceLabel = notification.source ? VACCINE_APPROVAL_SOURCE_LABELS[notification.source] : undefined;
        return sourceLabel || 'Vaccine Approvals';
    }

    if (notification.type === UserNotificationType.BOOKING) return 'Bookings';
    if (notification.type === UserNotificationType.BOOKING_REQUEST) return 'Online Booking';
    if (notification.type === UserNotificationType.VACCINE_EXPIRED) return 'Pets';
    if (notification.type === UserNotificationType.INTAKE_FORM) return 'Clients';
    if (notification.type === UserNotificationType.REVIEW) return 'Reviews';
    if (notification.type === UserNotificationType.MEMBERSHIP_PAYMENT_ISSUE) return 'Memberships';
    if (notification.type === UserNotificationType.WAITLIST_REQUEST) return 'Waitlist';

    return 'None';
};

interface Props {
    notification: UserNotification;
    onRedirect?: () => void;
    onOpenPetCard?: (petId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'width': '100%',
            'paddingTop': 25,
            'paddingBottom': 25,
            'cursor': 'pointer',
            'borderBottom': 'solid 1px #D4D4D4',

            [theme.breakpoints.down('md')]: {
                paddingTop: 20,
                paddingBottom: 20
            },
            [theme.breakpoints.down('sm')]: {
                paddingTop: 15,
                paddingBottom: 14
            },

            '&:hover': {
                backgroundColor: '#F8F8F8'
            }
        },
        gridContainer: {
            margin: 0
        },
        notificationMeta: {
            gap: 4,
            display: 'flex',
            flexDirection: 'column'
        },
        notificationHeader: {
            color: '#7A7A7A',
            cursor: 'pointer',
            fontSize: '13px'
        },
        notificationTitle: {
            'cursor': 'pointer !important',
            'color': '#000000',
            'fontSize': '15px',
            'padding': '0px !important',
            'fontWeight': 600,
            '&.ql-editor > *': {
                cursor: 'pointer !important'
            },
            '&.ql-editor > strong': {
                fontWeight: 600
            },
            '& strong, & b': {
                fontWeight: 600
            }
        },
        notificationDetail: {
            'cursor': 'pointer',
            'color': '#000000',
            'fontSize': '14px',
            'padding': '0px !important',
            '&.ql-editor > *': {
                cursor: 'pointer !important'
            },
            '& strong, & b': {
                fontWeight: 600
            }
        },
        dot: {
            borderRadius: '50%',
            height: '12px',
            width: '12px',
            backgroundColor: '#EAB464',
            pointerEvents: 'none',
            [theme.breakpoints.down('sm')]: {
                position: 'relative',
                top: '5px'
            }
        },
        iconAvatar: {
            [theme.breakpoints.down('sm')]: {
                width: '42px',
                height: '42px'
            }
        },
        subIcon: {
            width: '18px !important',
            height: '18px !important',
            [theme.breakpoints.down('sm')]: {
                width: '14px !important',
                height: '14px !important'
            }
        }
    })
);

// eslint-disable-next-line
const secret: string = `${process.env.REACT_APP_GLOBAL_SECRET}`;

export const NotificationRow: FunctionComponent<Props> = props => {
    const classes = useStyles(props);

    const timeZone = useTimeZone();
    const history = useHistory();
    const location = useLocation();
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const {
        hasGrooming,
        hasMobileGrooming,
        hasDental,
        hasDaycare,
        hasBoarding,
        hasEvaluation,
        hasTransportation,
        hasTraining,
        hasWalking
    } = useServiceCategories();

    const [redirect, setRedirect] = useState(false);
    const [booking, setBooking] = useState<Booking>();
    const [isLoading, setIsLoading] = useState(false);

    const notificationAmount = useSelector<RootState, number>(state => state.userNotifications.unread.length);

    const bookingsState = useSelector((state: RootState) => state.bookings);

    const newBookingsStatus = useSelector((state: RootState) => state.newBookings.status);

    const appointmentIdStore = useSelector<RootState, number | undefined>(state => state.appointments.appointment?.id);
    const orderIdStore = useSelector<RootState, number | undefined>(state => state.appointments.appointment?.orderId);

    const bookingIdStore = useSelector<RootState, number | undefined>(
        state => state.appointments.appointment?.bookingId
    );

    const appointmentStatusStore = useSelector<RootState, number | undefined>(
        state => state.appointments.appointment?.status?.id
    );

    const appointmentBusinessAreaStore = useSelector<RootState, string | undefined>(
        state => state.appointments.appointment?.services?.[0]?.businessArea?.id
    );

    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);

    const calendarAppointments = useSelector<RootState, Array<Appointment>>(state => state.appointments.appointments);

    const bookingStatus = booking?.appointments[0].status.id || props.notification.bookingStatus;

    const getBooking = () => {
        dispatch(getBookingThunk(apiClientWrapper, props.notification.bookingId!));
    };

    // Resolves the Bookings module + tab a booking should open in, falling back
    // to the always-available "all" module when the booking's service category
    // is not enabled for this marketplace (SU-2165).
    const resolveBookingDestination = (statusId?: number, businessArea?: string): BookingDestination => {
        const categoryAvailability: Record<string, boolean> = {
            grooming: hasGrooming || hasMobileGrooming || hasDental,
            daycare: hasDaycare,
            boarding: hasBoarding,
            evaluation: hasEvaluation,
            transportation: hasTransportation,
            training: hasTraining,
            walking: hasWalking
        };

        const destination = getBookingDestination(statusId, businessArea);

        if (destination.listType in categoryAvailability && !categoryAvailability[destination.listType]) {
            return getBookingDestination(statusId, null);
        }

        return destination;
    };

    const openOrder = (bookingId: number, orderId?: number, statusId?: number, businessArea?: string) => {
        setRedirect(false);

        if (location.pathname === '/calendar') {
            const params = new URLSearchParams();
            params.set('bookingId', String(bookingId));
            if (orderId) params.set('orderId', String(orderId));
            history.replace(`/calendar?${params.toString()}`);
        } else {
            const hash = encodeURIComponent(
                AES.encrypt(
                    JSON.stringify({
                        orderId: orderId,
                        bookingId: bookingId
                    }),
                    secret
                ).toString()
            );

            const { listType, tabId } = resolveBookingDestination(statusId, businessArea);
            history.replace(`/bookings/${listType}/${hash}?tab=${tabId}`);
        }

        props.onRedirect?.();
    };

    const openClient = (clientId: number) => {
        const hash = encodeURIComponent(
            AES.encrypt(
                JSON.stringify({
                    clientId: clientId
                }),
                secret
            ).toString()
        );

        setRedirect(false);
        history.replace(`/clients/${hash}`);
        props.onRedirect?.();
    };

    const markAsRead = () => {
        dispatch(updateThunk(props.notification.receiptId!, true, true));

        if (!props.notification.readAt) {
            dispatch(setNotificationBellAmount(notificationAmount - 1));
        }
    };

    const clickHandler = () => {
        if (props.notification.waitlistId) {
            setRedirect(false);
            markAsRead();
            dispatch(openWaitlistPanel('all', [props.notification.marketplaceId], props.notification.waitlistId));
            props.onRedirect?.();
        } else if (props.notification.appointmentId) {
            dispatch(getAppointmentThunk(apiClientWrapper, props.notification.appointmentId));
            setRedirect(true);
        } else if (booking?.id) {
            setRedirect(true);
            markAsRead();
            const appointment = booking.appointments[0];
            openOrder(
                booking.id,
                appointment?.orderId,
                appointment?.status?.id,
                appointment?.services?.[0]?.businessArea?.id
            );
        } else if (!booking && props.notification.bookingId) {
            const loaded = calendarAppointments.find(a => a.bookingId === props.notification.bookingId);

            if (loaded?.orderId) {
                markAsRead();
                openOrder(loaded.bookingId, loaded.orderId, loaded.status?.id, loaded.services?.[0]?.businessArea?.id);
            } else {
                setIsLoading(true);
                setRedirect(true);
                getBooking();
            }
        } else if (props.notification.enrollmentId) {
            setRedirect(false);
            markAsRead();
            history.replace(`/clients?enrollmentId=${props.notification.enrollmentId}#sold-memberships`);
            props.onRedirect?.();
        } else if (props.notification.clientId) {
            setRedirect(true);
            markAsRead();
            openClient(props.notification.clientId);
        } else if (props.notification.petId) {
            markAsRead();
            props.onOpenPetCard?.(props.notification.petId);
        } else if (props.notification.type === UserNotificationType.REVIEW) {
            setRedirect(false);
            markAsRead();
            history.replace(MarketingReviewsPath);
            props.onRedirect?.();
        } else if (props.notification.type === UserNotificationType.VACCINE_APPROVAL) {
            setRedirect(false);
            markAsRead();
            const vaccineApprovalId = props.notification.vaccineApprovalId;
            history.replace(
                vaccineApprovalId
                    ? `/clients?vaccineApprovalId=${vaccineApprovalId}#vaccine-approvals`
                    : VaccineApprovalsPath
            );
            props.onRedirect?.();
        }
    };

    useNonInitialEffect(() => {
        if (appointmentsStatus === AppointmentsStatus.GetSuccess && redirect === true) {
            if (appointmentIdStore && bookingIdStore) {
                if (props.notification.receiptId) {
                    markAsRead();
                }

                openOrder(bookingIdStore, orderIdStore, appointmentStatusStore, appointmentBusinessAreaStore);
            }
        }

        if (
            bookingsState.status === BookingsStatus.GetSuccess &&
            bookingsState.booking?.id === props.notification.bookingId
        ) {
            setIsLoading(false);
            setBooking(bookingsState.booking);

            if (redirect) {
                markAsRead();
                const appointment = bookingsState.booking?.appointments[0];
                openOrder(
                    Number(bookingsState.booking?.id),
                    appointment?.orderId,
                    appointment?.status?.id,
                    appointment?.services?.[0]?.businessArea?.id
                );
            }
        }
    }, [appointmentsStatus, bookingsState.status]);

    const subIcon = [
        UserNotificationType.BOOKING_REQUEST,
        UserNotificationType.VACCINE_EXPIRED,
        UserNotificationType.VACCINE_APPROVAL,
        UserNotificationType.WAITLIST_REQUEST
    ].includes(props.notification.type)
        ? undefined
        : [UserNotificationSubtype.CONFIRMED, undefined].includes(props.notification.subtype)
        ? faCircleCheck
        : faCircleXmark;

    const shouldShowActions =
        props.notification.type === UserNotificationType.BOOKING_REQUEST && bookingStatus === status.REQUESTED;

    const shouldShowBookingStatus =
        props.notification.type === UserNotificationType.BOOKING_REQUEST && bookingStatus !== status.REQUESTED;

    const isVaccineApproval = props.notification.type === UserNotificationType.VACCINE_APPROVAL;
    const shouldShowVaccineApprovalStatus =
        isVaccineApproval &&
        ['approved', 'rejected', 'declined'].includes((props.notification.status || '').toLowerCase());

    const notificationSubject = useMemo(() => {
        const subject = props.notification.subject as string;
        return isVaccineApproval ? boldVaccineApprovalNames(subject) : subject;
    }, [isVaccineApproval, props.notification.subject]);

    const notificationBody = useMemo(() => {
        const body = props.notification.body as string;
        return isVaccineApproval ? boldVaccineApprovalNames(body) : body;
    }, [isVaccineApproval, props.notification.body]);

    return (
        <>
            <Box className={classes.container}>
                <Grid
                    container
                    justifyContent={'flex-end'}
                    className={classes.gridContainer}
                    onClick={clickHandler}
                    spacing={1}
                >
                    <Grid
                        item
                        xs={2}
                    >
                        <Grid
                            container
                            direction="row-reverse"
                        >
                            <IconAvatar
                                className={classes.iconAvatar}
                                icon={
                                    props.notification.type === UserNotificationType.INTAKE_FORM
                                        ? faMemoCircleCheck
                                        : props.notification.type === UserNotificationType.BOOKING_REQUEST
                                        ? faBellConcierge
                                        : props.notification.type === UserNotificationType.VACCINE_APPROVAL ||
                                          props.notification.type === UserNotificationType.VACCINE_EXPIRED
                                        ? faSyringe
                                        : props.notification.type === UserNotificationType.REVIEW
                                        ? faStar
                                        : props.notification.type === UserNotificationType.WAITLIST_REQUEST
                                        ? faClockRotateLeft
                                        : faCalendarAlt
                                }
                                subIcon={subIcon}
                                subIconClassName={classes.subIcon}
                                subIconColor={
                                    [UserNotificationSubtype.CONFIRMED, undefined].includes(props.notification.subtype)
                                        ? '#51A372'
                                        : '#EF4F57'
                                }
                                color={
                                    props.notification.type === UserNotificationType.VACCINE_EXPIRED
                                        ? '#FFFFFF'
                                        : '#000000'
                                }
                                bgColor={
                                    props.notification.type === UserNotificationType.VACCINE_EXPIRED
                                        ? '#FF6C73'
                                        : '#FAEFDF'
                                }
                                size={58}
                            ></IconAvatar>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        xs={9}
                        className={classes.notificationMeta}
                    >
                        <Grid container>
                            <Typography className={classes.notificationHeader}>
                                {(props.notification.sentAt
                                    ? props.notification.sentAt
                                    : props.notification.createdAt
                                ).format('DD-MM-YYYY') === moment().tz(timeZone).format('DD-MM-YYYY')
                                    ? (props.notification.sentAt
                                          ? props.notification.sentAt
                                          : props.notification.createdAt
                                      ).fromNow()
                                    : (props.notification.sentAt
                                          ? props.notification.sentAt
                                          : props.notification.createdAt
                                      ).format('MMM DD, YYYY')}{' '}
                                • {getNotificationCategoryLabel(props.notification)}
                            </Typography>
                        </Grid>

                        <Grid container>
                            <QuillContent
                                className={classes.notificationTitle}
                                content={notificationSubject}
                            />
                        </Grid>

                        <Grid container>
                            <QuillContent
                                className={classes.notificationDetail}
                                content={notificationBody}
                            />
                        </Grid>

                        {shouldShowBookingStatus && (
                            <Grid container>
                                <NotificationRowRequestStatus
                                    bookingStatus={bookingStatus || props.notification.bookingStatus}
                                />
                            </Grid>
                        )}

                        {shouldShowVaccineApprovalStatus && (
                            <Grid container>
                                <NotificationRowVaccineApprovalStatus status={props.notification.status} />
                            </Grid>
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={1}
                    >
                        {isLoading ? (
                            <CircularProgress
                                size={12}
                                style={{ color: '#EAB464' }}
                            />
                        ) : (
                            !props.notification.openedAt && <Box className={classes.dot}></Box>
                        )}
                    </Grid>
                </Grid>

                {shouldShowActions && (
                    <Grid
                        container
                        justifyContent={'flex-end'}
                        className={classes.gridContainer}
                        spacing={1}
                    >
                        <Grid
                            item
                            xs={2}
                        ></Grid>
                        <Grid
                            item
                            xs={9}
                        >
                            <NotificationRowActions
                                loading={isLoading}
                                notification={props.notification}
                                onStatusChange={() => setIsLoading(true)}
                                onStatusChanged={() => {
                                    setIsLoading(true);
                                    getBooking();
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={1}
                        ></Grid>
                    </Grid>
                )}
            </Box>
        </>
    );
};

export default NotificationRow;
